import { useRef, useEffect } from 'react';
import { useI18n } from '../i18n/I18nContext';

const CENTER = { x: 280, y: 65 };

type OrbitDef = {
  rx: number;
  ry: number;
  color: string;
  glow: string;
  period: number;
  startAngle: number;
  dashed: boolean;
};

const ORBITS: OrbitDef[] = [
  { rx: 178, ry: 48, color: '#ff5500', glow: '#ff5500', period: 9000, startAngle: Math.PI, dashed: true },
  { rx: 178, ry: 48, color: '#fdcb12', glow: '#fdcb12', period: 9000, startAngle: 0, dashed: true },
  { rx: 220, ry: 62, color: '#ff5500', glow: '#ff5500', period: 13000, startAngle: Math.PI * 0.7, dashed: true },
  { rx: 220, ry: 62, color: '#fdcb12', glow: '#fdcb12', period: 13000, startAngle: Math.PI * 1.7, dashed: true },
];

export default function StarSystem() {
  const { t } = useI18n();
  const sphereRefs = useRef<(SVGGElement | null)[]>([]);
  const lineRefs = useRef<(SVGPathElement | null)[]>([]);
  const energyRefs = useRef<(SVGPathElement | null)[]>([]);
  const startRef = useRef<number>(0);

  useEffect(() => {
    let raf = 0;
    startRef.current = performance.now();

    const tick = (now: number) => {
      const elapsed = now - startRef.current;

      ORBITS.forEach((orbit, i) => {
        const angle = orbit.startAngle + (elapsed / orbit.period) * Math.PI * 2;
        const x = CENTER.x + orbit.rx * Math.cos(angle);
        const y = CENTER.y + orbit.ry * Math.sin(angle);

        const sphere = sphereRefs.current[i];
        if (sphere) {
          sphere.setAttribute('transform', `translate(${x - CENTER.x}, ${y - CENTER.y})`);
        }

        const cx1 = CENTER.x + (x - CENTER.x) * 0.35;
        const cy1 = CENTER.y + (y - CENTER.y) * 0.15;
        const pathD = `M${CENTER.x} ${CENTER.y} Q${cx1} ${cy1} ${x} ${y}`;

        const line = lineRefs.current[i];
        if (line) {
          line.setAttribute('d', pathD);
        }

        const energy = energyRefs.current[i];
        if (energy) {
          energy.setAttribute('d', pathD);
          const dashLen = 200;
          energy.style.strokeDasharray = `${dashLen}`;
          energy.style.strokeDashoffset = `${dashLen - ((elapsed / 2000) * dashLen) % (dashLen * 2)}`;
        }
      });

      raf = requestAnimationFrame(tick);
    };

    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, []);

  return (
    <div className="star-system-wrap max-w-xl mx-auto mb-8" aria-label="Animated Star Model showing connected business units">
      <svg className="star-system w-full h-40" viewBox="0 0 560 150" fill="none" role="img">
        <defs>
          <radialGradient id="starGlow">
            <stop stopColor="#FDCB12" stopOpacity=".42" />
            <stop offset="1" stopColor="#FDCB12" stopOpacity="0" />
          </radialGradient>
          <filter id="starShadow" x="-100%" y="-100%" width="300%" height="300%">
            <feGaussianBlur stdDeviation="4" result="blur" />
            <feMerge><feMergeNode in="blur" /><feMergeNode in="SourceGraphic" /></feMerge>
          </filter>
        </defs>

        <circle className="star-system__halo" cx={CENTER.x} cy={CENTER.y} r="52" fill="url(#starGlow)" />

        <ellipse className="star-system__orbit" cx={CENTER.x} cy={CENTER.y} rx={178} ry={48} stroke="rgba(253,203,18,.22)" />
        <ellipse className="star-system__orbit star-system__orbit--outer" cx={CENTER.x} cy={CENTER.y} rx={220} ry={62} stroke="rgba(255,85,0,.16)" />

        {ORBITS.map((orbit, i) => (
          <g key={`track-${i}`}>
            <path
              ref={(el) => { lineRefs.current[i] = el; }}
              className="star-system__conn"
              d=""
              stroke={orbit.color}
              strokeWidth="1.5"
              fill="none"
              opacity=".35"
            />
            <path
              ref={(el) => { energyRefs.current[i] = el; }}
              className="star-system__energy"
              d=""
              stroke={orbit.color}
              strokeWidth="2.5"
              fill="none"
              strokeLinecap="round"
              opacity=".8"
            />
            <g
              ref={(el) => { sphereRefs.current[i] = el; }}
              className="star-system__sphere"
            >
              <circle cx={CENTER.x} cy={CENTER.y} r="11" fill={orbit.glow} opacity=".15" />
              <circle cx={CENTER.x} cy={CENTER.y} r="5.5" fill={orbit.color} />
            </g>
          </g>
        ))}

        <g className="star-system__core" filter="url(#starShadow)">
          <path d="m280 39 7.4 15 16.6 2.4-12 11.7 2.8 16.5-14.8-7.8-14.8 7.8 2.8-16.5-12-11.7 16.6-2.4L280 39Z" fill="#FDCB12" />
        </g>
      </svg>
      <div className="star-system__caption" aria-hidden="true">
        <span>{t.starModel.captionLeft}</span>
        <span className="star-system__caption-line" />
        <span>{t.starModel.captionRight}</span>
      </div>
    </div>
  );
}
